import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ApiError,
  tuya,
  type NoveTuyaZariadenie,
  type PrihlasenieTuyaCloud,
  type TuyaAppRegion,
  type TuyaRegion,
  type VysledokSynchronizacieUctu,
  type ZaciatokPrihlaseniaQr,
  type TuyaZariadenie,
  type VysledokScanu,
  type VysledokSynchronizacie,
} from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Field,
  Notice,
  PageHeader,
  SectionTitle,
  StatusDot,
} from '../components/ui.js';
import {
  IconChevronRight,
  IconPlug,
  IconDownload,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data, farbaTonu } from './common.js';

/*
 * Zariadenia Tuya.
 *
 * Vlastna obrazovka preto, ze zariadenie bez lokalneho kluca este nie je
 * zariadenim v modeli - brana o nom vie z discovery, ale necita z neho
 * ani jednu hodnotu. Kym kluc nezada pouzivatel, nema sa co zobrazit
 * v beznom zozname.
 */

/** Ako casto sa zoznam sam obnovuje, kym je obrazovka otvorena. */
const INTERVAL_ZOZNAMU_MS = 10_000;

const POPIS_STAVU: Record<string, string> = {
  unconfigured: 'Chýba lokálny kľúč',
  discovering: 'Hľadám v sieti',
  connecting: 'Pripájam sa',
  connected: 'Pripojené',
  backoff: 'Čakám na ďalší pokus',
  stopped: 'Zastavené',
};

/**
 * Preco zariadenie nie je pripojene.
 *
 * Rozdiel medzi „kluc neberie" a „neodpoveda" urcuje, kde hladat: prvy
 * pripad sa riesi stiahnutim klucov, druhy sietou.
 */
const POPIS_PRICINY: Record<string, string> = {
  'auth-failed':
    'Zariadenie odpovedá, ale nepáči sa mu náš lokálny kľúč. Po prepárovaní v aplikácii Tuya sa kľúč mení — dotiahni ho znova zo synchronizácie účtu alebo z Tuya IoT.',
  unreachable:
    'Zariadenie neodpovedá na lokálnom porte 6668. Býva to iná podsieť alebo VLAN, zmenená adresa, alebo je odpojené od napájania.',
  stopped: 'Spojenie je zastavené.',
  'never-seen': 'Zariadenie sa zatiaľ ani raz neozvalo.',
};

/** Datove centra API mobilnej aplikacie; su INE nez pri Tuya IoT. */
const POPIS_REGIONU: Record<TuyaAppRegion, string> = {
  eu: 'Európa',
  us: 'Amerika',
  cn: 'Čína',
  in: 'India',
};

const TON_STAVU = (stav: string, maKluc: boolean): 'ok' | 'warn' | 'danger' | 'neutral' => {
  if (stav === 'connected') return 'ok';
  if (!maKluc) return 'warn';
  if (stav === 'backoff') return 'danger';
  return 'neutral';
};

/**
 * Farba textu stavu.
 *
 * Bodka sama o sebe je mala; v dlhom zozname sa stav hlada ocami
 * a farba textu je to, co ho odlisi na prvy pohlad (hlasene 2026-09-18).
 */
const TEXT_STAVU = (stav: string, maKluc: boolean): string => farbaTonu(TON_STAVU(stav, maKluc));

function FormularKluca({
  device,
  poUlozeni,
}: {
  readonly device: TuyaZariadenie;
  readonly poUlozeni: () => void;
}): ReactNode {
  const [kluc, setKluc] = useState('');
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setPrebieha(true);
    void tuya
      .setKey(device.deviceId, kluc)
      .then(() => {
        setKluc('');
        poUlozeni();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kľúč sa nepodarilo uložiť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <form onSubmit={odosli} className="flex flex-col gap-3">
      <Field
        label="Lokálny kľúč"
        value={kluc}
        onChange={setKluc}
        type="password"
        required
        autoComplete="off"
        hint="Získaš ho zo synchronizácie účtu alebo z Tuya IoT Platform. Uloží sa zašifrovaný; zobraziť ho môže len správca a každé zobrazenie ide do auditu."
      />
      <Profil device={device} />

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      <div>
        <Button type="submit" variant="primary" disabled={prebieha || kluc.trim() === ''}>
          {prebieha ? 'Ukladám a pripájam…' : 'Uložiť kľúč a pripojiť'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Jeden riadok zoznamu zariadeni.
 *
 * Na sirsej obrazovke stlpce vedla seba, na mobile sa zabali pod nazov -
 * tabulka by sa na telefone nedala precitat. Podrobnosti su az po
 * kliknuti, aby sa dal zoznam prebehnut ocami (predloha: tuya-local-key,
 * ziadost zadavatela 2026-09-18).
 */
function RiadokZariadenia({
  device,
  otvorene,
  onPrepni,
}: {
  readonly device: TuyaZariadenie;
  readonly otvorene: boolean;
  readonly onPrepni: () => void;
}): ReactNode {
  return (
    <li>
      <button
        type="button"
        onClick={onPrepni}
        aria-expanded={otvorene}
        className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-(--color-border) px-3 py-2.5 text-left last:border-b-0 hover:bg-(--color-surface-sunken) sm:grid-cols-[minmax(0,2.2fr)_7rem_minmax(0,2.4fr)_minmax(0,1.4fr)_auto] ${
          otvorene ? 'bg-(--color-surface-sunken)' : ''
        }`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="shrink-0 text-(--color-text-subtle)">
            <IconPlug className="size-4" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-(--color-text)">
              {device.name ?? device.deviceId}
            </span>
            {/*
              Na mobile je stav aj identifikator pod nazvom; stlpce sa tam
              nezmestia. Stav nesie FARBU aj tu - bez nej sa v zozname
              nedalo na prvy pohlad povedat, co je v poriadku a co nie
              (hlasene 2026-09-18).
            */}
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs sm:hidden">
              <span className="inline-flex items-center gap-1.5">
                <StatusDot tone={TON_STAVU(device.state, device.hasLocalKey)} />
                <span className={TEXT_STAVU(device.state, device.hasLocalKey)}>
                  {POPIS_STAVU[device.state] ?? device.state}
                </span>
              </span>
              <span className="truncate font-mono text-(--color-text-subtle)">
                {device.deviceId}
              </span>
            </span>
          </span>
        </span>

        <span className="hidden sm:flex sm:items-center sm:gap-1.5">
          <StatusDot tone={TON_STAVU(device.state, device.hasLocalKey)} />
          <span className={`truncate text-xs ${TEXT_STAVU(device.state, device.hasLocalKey)}`}>
            {POPIS_STAVU[device.state] ?? device.state}
          </span>
        </span>
        <span className="hidden truncate font-mono text-xs text-(--color-text-muted) sm:block">
          {device.deviceId}
        </span>
        <span className="hidden truncate text-xs text-(--color-text-muted) sm:block">
          {device.ip ?? 'adresa neznáma'}
        </span>

        <span className="text-(--color-text-subtle)">
          <IconChevronRight
            className={`size-4 transition-transform ${otvorene ? 'rotate-90' : ''}`}
          />
        </span>
      </button>
    </li>
  );
}

/**
 * Podrobnosti jedneho zariadenia.
 *
 * Clenenie je rovnake ako v predlohe: identita, spojenie, kluc a az
 * potom zasahy. Odobrat sa da VZDY, aj zariadenie bez kluca - take
 * vznikne z ohlasenia v sieti a v zozname zavadzia.
 */
function DetailZariadenia({
  device,
  poZmene,
  zavri,
}: {
  readonly device: TuyaZariadenie;
  readonly poZmene: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [zabuda, setZabuda] = useState(false);
  /*
   * Odobranie sa potvrdzuje rovnako ako zabudnutie kluca. Je to
   * destruktivnejsie z tych dvoch - zmaze aj kluc - takze by bolo
   * cudne pytat sa pri slabsom a nie pri silnejsom.
   */
  const [odobera, setOdobera] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const odober = (): void => {
    setChyba(undefined);
    void tuya
      .remove(device.deviceId)
      .then(() => {
        setOdobera(false);
        zavri();
        poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenie sa nepodarilo odobrať.');
      });
  };

  const zabudni = (): void => {
    setChyba(undefined);
    void tuya
      .forgetKey(device.deviceId)
      .then(() => {
        setZabuda(false);
        poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kľúč sa nepodarilo zabudnúť.');
      });
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-base font-medium break-words text-(--color-text)">
            {device.name ?? device.deviceId}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={TON_STAVU(device.state, device.hasLocalKey)}>
              {POPIS_STAVU[device.state] ?? device.state}
            </Badge>
            {/*
              Chybajuci kluc uz hovori stav („Chýba lokálny kľúč"),
              takze druhy odznak by to len zopakoval. Ukazuje sa preto
              len to, co stav nepovie (hlasene 2026-09-24).
            */}
            {device.state !== 'unconfigured' && device.hasLocalKey && (
              <Badge tone="ok">kľúč uložený</Badge>
            )}
          </div>
        </div>
        <Button variant="ghost" onClick={zavri}>
          Zavrieť
        </Button>
      </div>

      {/*
       * PRECO nie je pripojene.
       *
       * „Kľúč neberie" a „neodpovedá" sú dve úplne iné veci a bez toho
       * rozdielu sa chyba hľadá na nesprávnej strane: zariadenie funkčné
       * v aplikácii Tuya býva po prepárovaní jednoducho s iným kľúčom
       * (2026-09-17).
       */}
      {device.cause !== undefined && device.state !== 'connected' && (
        <Notice tone="warn">
          <p>{POPIS_PRICINY[device.cause]}</p>
          {/*
            Presna hlaska zo zariadenia. „Neodpovedá" je zaver, nie
            dokaz: ECONNRESET, vyprsany strop a odmietnuty port su tri
            rozne pribehy (hlasene 2026-09-25).
          */}
          {device.lastError !== undefined && (
            <p className="mt-2 font-mono text-xs break-all">
              {device.lastError}
              {device.attempts !== undefined && device.attempts > 0 && (
                <span className="font-sans"> · neúspešných pokusov: {device.attempts}</span>
              )}
              {device.lastFailureAt !== undefined && (
                <span className="font-sans">
                  {' '}
                  · naposledy {new Date(device.lastFailureAt).toLocaleTimeString('sk-SK')}
                </span>
              )}
            </p>
          )}
        </Notice>
      )}

      <dl>
        <DataRow label="Identifikátor">
          <span className="font-mono text-xs break-all">{device.deviceId}</span>
        </DataRow>
        <DataRow label="Adresa">{device.ip ?? 'neznáma'}</DataRow>
        <DataRow label="Protokol">{device.version ?? 'neznámy'}</DataRow>
        <DataRow label="Profil">{device.profileId ?? 'bez profilu'}</DataRow>
      </dl>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      {!device.hasLocalKey && !jeSpravca && (
        <Notice tone="warn">Lokálny kľúč môže zadať len správca.</Notice>
      )}
      {!device.hasLocalKey && jeSpravca && <FormularKluca device={device} poUlozeni={poZmene} />}

      {device.hasLocalKey && jeSpravca && <Kluc device={device} />}

      <div className="flex flex-wrap items-center gap-2">
        {device.hasLocalKey && device.loxlinkId !== undefined && (
          <Link
            to={`/zariadenia/${device.loxlinkId}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-(--color-accent)"
          >
            Otvoriť zariadenie
            <IconChevronRight className="size-4" />
          </Link>
        )}

        {jeSpravca && zabuda && (
          <>
            <Button variant="danger" onClick={zabudni}>
              Naozaj zabudnúť kľúč
            </Button>
            <Button variant="ghost" onClick={() => setZabuda(false)}>
              Nechať
            </Button>
          </>
        )}

        {jeSpravca && odobera && (
          <>
            <Button variant="danger" onClick={odober}>
              {device.hasLocalKey ? 'Naozaj odobrať aj s kľúčom' : 'Naozaj odobrať'}
            </Button>
            <Button variant="ghost" onClick={() => setOdobera(false)}>
              Nechať
            </Button>
          </>
        )}

        {jeSpravca && !zabuda && !odobera && (
          <>
            {device.hasLocalKey && (
              <Button variant="ghost" onClick={() => setZabuda(true)}>
                Zabudnúť kľúč
              </Button>
            )}
            <Button
              variant="danger"
              icon={<IconTrash className="size-4" />}
              onClick={() => setOdobera(true)}
              title={
                device.hasLocalKey
                  ? 'Odoberie zariadenie aj s jeho kľúčom. Kým vysiela v sieti, objaví sa v zozname znova — už bez kľúča.'
                  : 'Odoberie zariadenie zo zoznamu. Kým vysiela v sieti, objaví sa znova.'
              }
            >
              Odobrať
            </Button>
          </>
        )}
      </div>

      <Profil device={device} />
    </Card>
  );
}

/**
 * Diagnostika discovery.
 *
 * Ked sa nenajde nic, treba ROZLISIT tri veci: nechodia ziadne pakety,
 * chodia ale nedaju sa precitat, alebo sa nepodarilo poslat vyzvu.
 * Najcastejsia pricina prveho pripadu je kontejner bez `network_mode:
 * host` - UDP broadcast tam nedorazi a automaticky sa nenajde nikdy.
 */
function Diagnostika({
  prazdne,
  znameAdresy,
  poHladani,
}: {
  readonly prazdne: boolean;
  /** Adresy zariadeni, ktore uz brana pozna. */
  readonly znameAdresy: readonly string[];
  readonly poHladani: () => void;
}): ReactNode {
  const nacitanie = useApi((signal) => tuya.discovery(signal));
  const [hlada, setHlada] = useState(false);
  const [skenuje, setSkenuje] = useState(false);
  const [scan, setScan] = useState<VysledokScanu | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const hladajTeraz = () => {
    setChyba(undefined);
    setHlada(true);
    void tuya
      .solicit()
      .then(() => new Promise((r) => setTimeout(r, 2500)))
      .then(() => {
        nacitanie.refresh();
        // Aj ZOZNAM zariadeni, nie len diagnostiku. Bez toho vyzeralo
        // hladanie tak, ze nic nenaslo - odpoved uz prisla, ale zoznam
        // na obrazovke bol stary.
        poHladani();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Vyhľadávanie zlyhalo.');
      })
      .finally(() => setHlada(false));
  };

  /*
   * Prehliadka siete.
   *
   * Zariadenie, ktore sa neohlasuje, discovery nikdy nenajde - a presne
   * take chyba, ked ich v aplikacii vidno viac nez tu. Prehliadka skusi
   * spojenie na port 6668 na kazdej adrese v sieti; kto ho prijme, je
   * takmer iste zariadenie Tuya. Identifikator ani kluc sa tym zistit
   * neda, tie treba zadat rucne.
   */
  const preskenuj = (): void => {
    setChyba(undefined);
    setSkenuje(true);
    void tuya
      .scan()
      .then(({ scan: vysledok }) => {
        setScan(vysledok);
        poHladani();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Prehliadka siete zlyhala.');
      })
      .finally(() => setSkenuje(false));
  };

  const stav = nacitanie.data?.discovery;

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Vyhľadávanie v sieti</SectionTitle>
        <div className="flex flex-wrap gap-2">
          <Button onClick={hladajTeraz} disabled={hlada} icon={<IconRefresh className="size-4" />}>
            {hlada ? 'Hľadám…' : 'Vyhľadať teraz'}
          </Button>
          <Button onClick={preskenuj} disabled={skenuje} variant="ghost">
            {skenuje ? 'Prehliadam sieť…' : 'Prehliadať sieť'}
          </Button>
        </div>
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      {nacitanie.error !== undefined && <Notice tone="warn">{nacitanie.error}</Notice>}

      {stav && (
        <>
          <dl>
            <DataRow label="Stav">{stav.running ? 'Beží' : 'Nebeží'}</DataRow>
            <DataRow label="Počúva na portoch">
              {stav.boundPorts.length === 0 ? 'na žiadnom' : stav.boundPorts.join(', ')}
            </DataRow>
            <DataRow label="Rozhrania">
              {stav.interfaces.length === 0
                ? 'žiadne použiteľné'
                : stav.interfaces.map((i) => `${i.address} → ${i.broadcast}`).join(', ')}
            </DataRow>
            <DataRow label="Prijaté pakety">
              {stav.receivedCount}
              {stav.malformedCount > 0 && ` (z toho ${stav.malformedCount} nečitateľných)`}
            </DataRow>
            <DataRow label="Odoslané výzvy">
              {stav.solicitCount}
              {stav.ownProbeCount > 0 && ` (${stav.ownProbeCount} sa vrátilo späť)`}
            </DataRow>
            <DataRow label="Posledný paket">
              {stav.lastPacketAt === undefined
                ? 'žiadny'
                : new Date(stav.lastPacketAt).toLocaleTimeString('sk-SK')}
            </DataRow>
          </dl>

          {stav.sources.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-(--color-text)">
                Kto v sieti vysiela ({stav.sources.length})
              </span>
              <p className="text-xs text-(--color-text-muted)">
                Zariadenie, ktoré tu nie je, nevysiela nič. Býva vypnuté, v inej podsieti, alebo je
                to podriadené zariadenie za bránou (Zigbee, Bluetooth) — také po sieti nehovorí a
                cez LoxLink sa priamo pridať nedá.
              </p>
              <div className="min-w-0 overflow-x-auto">
                <table className="w-full min-w-100 text-left text-xs">
                  <thead className="text-(--color-text-subtle)">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Adresa</th>
                      <th className="py-1 pr-3 font-medium">Zariadenie</th>
                      <th className="py-1 pr-3 font-medium">Pakety</th>
                      <th className="py-1 font-medium">Naposledy</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-(--color-text-muted)">
                    {stav.sources.map((z) => (
                      <tr key={z.ip} className="border-t border-(--color-border)">
                        <td className="py-1 pr-3">{z.ip}</td>
                        <td className="py-1 pr-3">{z.gwId ?? '—'}</td>
                        <td className="py-1 pr-3">
                          {z.packets}
                          {z.malformed > 0 && ` (${z.malformed} nečitateľných)`}
                        </td>
                        <td className="py-1">{new Date(z.lastAt).toLocaleTimeString('sk-SK')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {scan !== undefined && <VysledokPrehliadky scan={scan} zname={znameAdresy} />}

          {stav.lastError !== undefined && <Notice tone="warn">{stav.lastError}</Notice>}

          {prazdne && stav.receivedCount === 0 && (
            <Notice tone="warn">
              <p className="font-medium">Neprišiel ani jeden paket.</p>
              <p className="mt-1">
                Zariadenia sa ohlasujú UDP broadcastom. Ten neprejde do kontajnera bez
                <code> network_mode: host</code>, ani medzi rozdielnymi podsieťami alebo cez VLAN.
                Kým to tak je, automaticky sa nenájde nič — zariadenie pridaj tlačidlom Pridať ručne
                v hlavičke stránky.
              </p>
            </Notice>
          )}

          {prazdne && stav.receivedCount > 0 && stav.receivedCount === stav.malformedCount && (
            <Notice tone="warn">
              Pakety prichádzajú, ale ani jeden sa nedá prečítať ako ohlásenie Tuya. Na týchto
              portoch môže vysielať aj iné zariadenie.
            </Notice>
          )}
        </>
      )}
    </Card>
  );
}

const PRAZDNE_ZARIADENIE = {
  deviceId: '',
  name: '',
  localKey: '',
  ip: '',
  version: '3.5' as const,
};

/*
 * Stiahnutie lokalnych klucov z Tuya IoT.
 *
 * Lokalny kluc sa v sieti NIKDE nezisti - zariadenie ho nikdy nepovie.
 * Bud sa prepisuje rucne z webu Tuya IoT po jednom, alebo sa stiahne
 * takto naraz. Access Secret ide na server, pouzije sa na volanie
 * a zahodi; ulozia sa len lokalne kluce.
 */
function SynchronizaciaKlucov({
  poSynchronizacii,
}: {
  readonly poSynchronizacii: () => void;
}): ReactNode {
  const nastavenia = useApi((signal) => tuya.cloudSettings(signal));
  const ulozene = nastavenia.data?.cloud;
  const [otvorene, setOtvorene] = useState(false);
  const [accessId, setAccessId] = useState('');
  const [accessSecret, setAccessSecret] = useState('');
  const [region, setRegion] = useState<TuyaRegion>('eu');
  const [zapamatat, setZapamatat] = useState(true);
  const [zadatIny, setZadatIny] = useState(false);
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [vysledok, setVysledok] = useState<VysledokSynchronizacie | undefined>(undefined);

  /*
   * Ulozene udaje sa predvyplnia, aby sa nemuseli pisat znova.
   * Tajomstvo NIE - to server nevracia a rozhranie ho nepozna.
   */
  useEffect(() => {
    if (ulozene?.configured !== true) return;
    setAccessId((doteraz) => (doteraz === '' ? (ulozene.accessId ?? '') : doteraz));
    if (ulozene.region !== undefined) setRegion(ulozene.region);
  }, [ulozene?.configured, ulozene?.accessId, ulozene?.region]);

  const prepniAutomatiku = (): void => {
    setChyba(undefined);
    setBezi(true);
    void tuya
      .setAutoFetch(ulozene?.autoFetch !== true)
      .then(() => nastavenia.refresh())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Nastavenie sa nepodarilo uložiť.');
      })
      .finally(() => setBezi(false));
  };

  const zabudni = (): void => {
    setChyba(undefined);
    void tuya
      .forgetCloud()
      .then(() => {
        setAccessId('');
        nastavenia.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Údaje sa nepodarilo zabudnúť.');
      });
  };

  const stiahni = (vstup: PrihlasenieTuyaCloud): void => {
    setChyba(undefined);
    setVysledok(undefined);
    setBezi(true);
    void tuya
      .cloudSync(vstup)
      .then(({ sync }) => {
        setVysledok(sync);
        // Tajomstvo v prehliadaci nedrzime dlhsie, nez treba.
        setAccessSecret('');
        nastavenia.refresh();
        poSynchronizacii();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kľúče sa nepodarilo stiahnuť.');
      })
      .finally(() => setBezi(false));
  };

  /** Stiahnutie uloženým prístupom — bez opätovného zadávania tajomstva. */
  const stiahniUlozenym = (): void => stiahni({});

  const odosli = (event: FormEvent): void => {
    event.preventDefault();
    stiahni({
      accessId: accessId.trim(),
      accessSecret: accessSecret.trim(),
      region,
      remember: zapamatat,
      autoFetch: true,
    });
  };

  if (!otvorene) {
    return (
      <Card className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-medium text-(--color-text)">Tuya IoT</span>
            <span className="text-xs text-(--color-text-muted)">
              {ulozene?.hasSecret === true
                ? `Prístup uložený${ulozene.autoFetch ? ' · kľúč k novému zariadeniu sa stiahne sám' : ''}`
                : 'Lokálny kľúč sa v sieti zistiť nedá — zariadenie ho nikdy nepovie.'}
            </span>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {ulozene?.hasSecret === true && (
              <Button variant="primary" onClick={stiahniUlozenym} disabled={bezi}>
                {bezi ? 'Sťahujem…' : 'Stiahnuť kľúče'}
              </Button>
            )}
            <Button
              variant={ulozene?.hasSecret === true ? 'ghost' : 'primary'}
              onClick={() => setOtvorene(true)}
            >
              {ulozene?.hasSecret === true ? 'Nastavenia' : 'Stiahnuť kľúče'}
            </Button>
          </div>
        </div>
        <VysledokSynchronizacieNotice chyba={chyba} vysledok={vysledok} />
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Tuya IoT</SectionTitle>

      {ulozene?.hasSecret === true && (
        <div className="flex flex-col gap-3">
          <dl>
            <DataRow label="Access ID">{ulozene.accessId ?? 'neznáme'}</DataRow>
            <DataRow label="Dátové centrum">{ulozene.region ?? 'neznáme'}</DataRow>
            <DataRow label="Prístupové tajomstvo">uložené, zašifrované</DataRow>
            <DataRow label="Kľúč k novému zariadeniu">
              {ulozene.autoFetch ? 'stiahne sa sám' : 'len na požiadanie'}
            </DataRow>
            <DataRow label="Naposledy stiahnuté">
              {ulozene.lastSyncAt === null
                ? 'zatiaľ nikdy'
                : new Date(ulozene.lastSyncAt).toLocaleString('sk-SK')}
            </DataRow>
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={stiahniUlozenym} disabled={bezi}>
              {bezi ? 'Sťahujem…' : 'Stiahnuť kľúče'}
            </Button>
            <Button onClick={prepniAutomatiku} disabled={bezi}>
              {ulozene.autoFetch ? 'Vypnúť automatiku' : 'Zapnúť automatiku'}
            </Button>
            <Button variant="danger" onClick={zabudni}>
              Zabudnúť prístup
            </Button>
          </div>
          <p className="text-xs text-(--color-text-muted)">
            Zabudnutie zmaže Access ID aj tajomstvo. Lokálne kľúče zariadení zostanú — brána funguje
            ďalej bez cloudu.
          </p>
        </div>
      )}

      <VysledokSynchronizacieNotice chyba={chyba} vysledok={vysledok} />

      {ulozene?.hasSecret === true && !zadatIny ? (
        <div className="flex flex-col gap-2">
          <Button variant="ghost" onClick={() => setZadatIny(true)}>
            Zadať iný prístup
          </Button>
          <p className="text-xs text-(--color-text-muted)">
            Použije sa namiesto uloženého — napríklad keď na Tuya IoT vznikne nový projekt.
          </p>
        </div>
      ) : (
        <>
          <Notice tone="warn">
            <p className="font-medium">
              {zapamatat ? 'Access Secret sa uloží.' : 'Access Secret sa neuloží.'}
            </p>
            <p className="mt-1">
              {zapamatat
                ? 'Uloží sa zašifrovaný, aby sa kľúče dali stiahnuť bez opätovného zadávania. Kto ho má, vie stiahnuť kľúče ku všetkým zariadeniam účtu — dá sa kedykoľvek zabudnúť.'
                : 'Použije sa na jedno volanie a zahodí; v úložisku zostanú len lokálne kľúče zariadení.'}
            </p>
          </Notice>

          <form className="flex flex-col gap-3" onSubmit={odosli}>
            <Field label="Access ID" value={accessId} onChange={setAccessId} required />
            <Field
              label="Access Secret"
              value={accessSecret}
              onChange={setAccessSecret}
              type="password"
              required
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-(--color-text)">Dátové centrum</span>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value as TuyaRegion)}
                className="rounded-(--radius-control) border border-(--color-border) bg-(--color-surface-raised) px-3 py-2 text-sm text-(--color-text)"
              >
                <option value="eu">Európa (centrálna)</option>
                <option value="eu-w">Európa (západná)</option>
                <option value="us">Amerika (západná)</option>
                <option value="us-e">Amerika (východná)</option>
                <option value="cn">Čína</option>
                <option value="in">India</option>
                <option value="sg">Singapur</option>
              </select>
              <span className="text-xs text-(--color-text-muted)">
                Musí sedieť s tým, kde projekt vznikol — inak API odmietne aj správny kľúč.
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-(--color-text)">
              <input
                type="checkbox"
                checked={zapamatat}
                onChange={(e) => setZapamatat(e.target.checked)}
                className="mt-1"
              />
              <span>
                Zapamätať prístup
                <span className="block text-xs text-(--color-text-muted)">
                  Kľúče sa potom dajú stiahnuť jedným tlačidlom, bez opätovného zadávania.
                </span>
              </span>
            </label>

            <div>
              <Button type="submit" variant="primary" disabled={bezi}>
                {bezi ? 'Sťahujem…' : 'Stiahnuť kľúče'}
              </Button>
            </div>
          </form>
        </>
      )}

      <div>
        <Button variant="ghost" onClick={() => setOtvorene(false)}>
          Zavrieť
        </Button>
      </div>
    </Card>
  );
}

/*
 * Ucty aplikacie Tuya - cesta ku klucom BEZ predplatneho Tuya IoT.
 *
 * Predplatne IoT Core ma skusobnu dobu a po nej prestane vydavat kluce
 * (zadavatel 2026-09-17, kod 28841002). Ucet aplikacie - ten isty, cez
 * ktory sa zariadenia parovali v telefone - vie zoznam zariadeni aj
 * s klucmi a nic sa zaň neplati. Uctov moze byt viac: kazda znacka ma
 * vlastnu aplikaciu, a teda vlastny ucet.
 */
function UctyAplikacie({ poImporte }: { readonly poImporte: () => void }): ReactNode {
  const nacitanie = useApi((signal) => tuya.appAccounts(signal));
  const [pridava, setPridava] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Účty aplikácie Tuya</SectionTitle>
        {!pridava && (
          <Button icon={<IconPlus className="size-4" />} onClick={() => setPridava(true)}>
            Pridať účet
          </Button>
        )}
      </div>
      <p className="text-xs text-(--color-text-muted)">
        Kľúče z účtu, cez ktorý sú zariadenia spárované v telefóne — bez predplatného Tuya IoT.
        Účtov môže byť viac, každá značka má vlastnú aplikáciu.
      </p>

      {pridava && <FormularUctu poPridani={nacitanie.reload} zavri={() => setPridava(false)} />}

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.accounts.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-(--color-text-muted)">
                Zatiaľ žiadny účet. Po pridaní stačí tlačidlo Synchronizovať — ukáže, koľko
                zariadení už poznáme a ktoré sú nové.
              </p>
            </Card>
          ) : (
            <ul className="flex flex-col gap-3">
              {data.accounts.map((u) => (
                <KartaUctu
                  key={u.id}
                  nazov={u.label}
                  popis={`${u.email} · ${POPIS_REGIONU[u.region]} · ${popisSynchronizacie(u.lastSyncAt)}`}
                  automatika={u.autoFetch}
                  prepniAutomatiku={(zapnut) =>
                    tuya.setAppAutoFetch(u.id, zapnut).then(() => undefined)
                  }
                  synchronizuj={() => tuya.syncAppAccount(u.id).then(({ sync }) => sync)}
                  importuj={(pridat, aktualizovat) =>
                    tuya.importAppDevices(u.id, pridat, aktualizovat).then((r) => r.import)
                  }
                  odober={() => tuya.removeAppAccount(u.id).then(() => undefined)}
                  poZmene={nacitanie.reload}
                  poImporte={poImporte}
                />
              ))}
            </ul>
          )
        }
      </Data>
    </section>
  );
}

/*
 * Ucty prihlasene QR kodom - oficialna cesta ku klucom.
 *
 * Je to to iste rozhranie, cez ktore sa k zariadeniam dostava Home
 * Assistant: ziadne predplatne IoT Core a ziadne tajomstvo znacky
 * vytiahnute z aplikacie. Pristup potvrdis naskenovanim kodu v Smart
 * Life a das si ho tam kedykolvek odobrat.
 */
function UctyQr({ poImporte }: { readonly poImporte: () => void }): ReactNode {
  const nacitanie = useApi((signal) => tuya.qrAccounts(signal));
  const [prihlasuje, setPrihlasuje] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Účty cez QR kód</SectionTitle>
        {!prihlasuje && (
          <Button icon={<IconPlus className="size-4" />} onClick={() => setPrihlasuje(true)}>
            Prihlásiť QR kódom
          </Button>
        )}
      </div>
      <p className="text-xs text-(--color-text-muted)">
        Oficiálna cesta: to isté rozhranie, cez ktoré berie kľúče Home Assistant. Netreba predplatné
        Tuya IoT ani tajomstvo značky — prístup potvrdíš naskenovaním kódu v aplikácii a rovnako
        ľahko ho tam zase odoberieš.
      </p>

      {prihlasuje && (
        <PrihlasenieQr poPrihlaseni={nacitanie.reload} zavri={() => setPrihlasuje(false)} />
      )}

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.accounts.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-(--color-text-muted)">
                Zatiaľ žiadny účet. Po prihlásení stačí tlačidlo Synchronizovať.
              </p>
            </Card>
          ) : (
            <ul className="flex flex-col gap-3">
              {data.accounts.map((u) => (
                <KartaUctu
                  key={u.id}
                  nazov={u.label}
                  popis={`kód účtu ${u.userCode} · ${popisSynchronizacie(u.lastSyncAt)}`}
                  automatika={u.autoFetch}
                  prepniAutomatiku={(zapnut) =>
                    tuya.setQrAutoFetch(u.id, zapnut).then(() => undefined)
                  }
                  synchronizuj={() => tuya.syncQrAccount(u.id).then(({ sync }) => sync)}
                  importuj={(pridat, aktualizovat) =>
                    tuya.importQrDevices(u.id, pridat, aktualizovat).then((r) => r.import)
                  }
                  odober={() => tuya.removeQrAccount(u.id).then(() => undefined)}
                  poZmene={nacitanie.reload}
                  poImporte={poImporte}
                />
              ))}
            </ul>
          )
        }
      </Data>
    </section>
  );
}

/** Ako casto sa brana pyta, ci uz je kod naskenovany. */
const INTERVAL_QR_MS = 3000;

/**
 * Prihlasenie QR kodom.
 *
 * Kod uctu najde clovek v aplikacii; LoxLink zaň vypyta prihlasovaci
 * token, vykresli ho ako QR kod a potom sa opakovane pyta, ci ho uz
 * niekto naskenoval. Kym nie, server odpoveda 409 - to nie je chyba.
 */
function PrihlasenieQr({
  poPrihlaseni,
  zavri,
}: {
  readonly poPrihlaseni: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [label, setLabel] = useState('Smart Life');
  const [userCode, setUserCode] = useState('');
  const [zaciatok, setZaciatok] = useState<ZaciatokPrihlaseniaQr | undefined>(undefined);
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const odosli = (event: FormEvent): void => {
    event.preventDefault();
    setChyba(undefined);
    setBezi(true);
    void tuya
      .startQrLogin(label.trim(), userCode.trim())
      .then(setZaciatok)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Prihlásenie sa nepodarilo začať.');
      })
      .finally(() => setBezi(false));
  };

  /*
   * Po vykresleni kodu sa uz len cakа. Otazka odchadza kazde tri
   * sekundy; 409 znamena „este nie" a ticho sa preskoci.
   */
  useEffect(() => {
    if (zaciatok === undefined) return;
    let zruseny = false;

    const timer = setInterval(() => {
      void tuya
        .finishQrLogin(label.trim(), userCode.trim(), zaciatok.token)
        .then(() => {
          if (zruseny) return;
          poPrihlaseni();
          zavri();
        })
        .catch((error: unknown) => {
          // 409 = zatial nenaskenovane; cokolvek ine uz stoji za oznam.
          if (error instanceof ApiError && error.status === 409) return;
          if (zruseny) return;
          setChyba(error instanceof Error ? error.message : 'Prihlásenie zlyhalo.');
        });
    }, INTERVAL_QR_MS);

    return () => {
      zruseny = true;
      clearInterval(timer);
    };
  }, [zaciatok, label, userCode, poPrihlaseni, zavri]);

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Prihlásenie QR kódom</SectionTitle>

      {zaciatok === undefined ? (
        <form className="flex flex-col gap-3" onSubmit={odosli}>
          <Field
            label="Názov"
            value={label}
            onChange={setLabel}
            required
            hint="Ako si účet pomenuješ ty."
          />
          <Field
            label="Kód účtu"
            value={userCode}
            onChange={setUserCode}
            required
            autoComplete="off"
            hint="V aplikácii Smart Life: Ja → ikona nastavení → Účet a bezpečnosť. Tuya podľa neho vie, komu kód patrí."
          />

          {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={bezi}>
              {bezi ? 'Pripravujem…' : 'Zobraziť QR kód'}
            </Button>
            <Button variant="ghost" onClick={zavri}>
              Zrušiť
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-(--color-text)">
            Otvor aplikáciu Smart Life, vpravo hore ťukni na skenovanie a naskenuj tento kód. Potom
            potvrď prístup — okno sa zavrie samo.
          </p>
          <img
            src={zaciatok.qr}
            alt="QR kód na prihlásenie do účtu Tuya"
            className="mx-auto size-64 max-w-full rounded-(--radius-control) bg-white p-2"
          />
          <p className="text-xs break-all text-(--color-text-subtle)">{zaciatok.qrObsah}</p>

          {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={zavri}>
              Zrušiť
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Karta jedneho uctu: prehlad, synchronizacia a vyber toho, co sa zapise.
 *
 * Spolocna pre obe cesty ku klucom - ucet aplikacie aj ucet prihlaseny
 * QR kodom. Lisia sa len tym, ako sa zakladaju a co maju v podtitulku;
 * porovnanie a potvrdenie vyberu je to iste, tak nech je aj kod jeden.
 */
function KartaUctu({
  nazov,
  popis,
  automatika,
  prepniAutomatiku,
  synchronizuj,
  importuj,
  odober,
  poZmene,
  poImporte,
}: {
  readonly nazov: string;
  readonly popis: string;
  /** Doplna brana kluce z tohto uctu sama? */
  readonly automatika: boolean;
  readonly prepniAutomatiku: (zapnut: boolean) => Promise<void>;
  readonly synchronizuj: () => Promise<VysledokSynchronizacieUctu>;
  readonly importuj: (
    pridat: readonly string[],
    aktualizovat: readonly string[],
  ) => Promise<{ added: number; updated: number }>;
  readonly odober: () => Promise<void>;
  readonly poZmene: () => void;
  readonly poImporte: () => void;
}): ReactNode {
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [vysledok, setVysledok] = useState<VysledokSynchronizacieUctu | undefined>(undefined);
  const [vybrane, setVybrane] = useState<readonly string[]>([]);
  const [naAktualizaciu, setNaAktualizaciu] = useState<readonly string[]>([]);
  const [zapisane, setZapisane] = useState<{ added: number; updated: number } | undefined>(
    undefined,
  );

  const prepni = (
    zoznam: readonly string[],
    nastav: (hodnota: readonly string[]) => void,
    deviceId: string,
  ): void => {
    nastav(
      zoznam.includes(deviceId) ? zoznam.filter((x) => x !== deviceId) : [...zoznam, deviceId],
    );
  };

  const spustiSync = (): void => {
    setChyba(undefined);
    setZapisane(undefined);
    setBezi(true);
    void synchronizuj()
      .then((sync) => {
        setVysledok(sync);
        /*
         * Predvolene je vybrane vsetko nove a vsetko, comu kluc CHYBA -
         * doplnenim sa nic neprepisuje. Zmeneny kluc predvoleny NIE JE:
         * to je zasah do funkcneho zariadenia a potvrdzuje ho clovek.
         */
        setVybrane(sync.newDevices.map((d) => d.deviceId));
        setNaAktualizaciu(sync.missingKeys.map((d) => d.deviceId));
        poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenia sa nepodarilo načítať.');
      })
      .finally(() => setBezi(false));
  };

  const zapis = (): void => {
    setChyba(undefined);
    setBezi(true);
    void importuj(vybrane, naAktualizaciu)
      .then((vysledokImportu) => {
        setZapisane(vysledokImportu);
        setVysledok(undefined);
        setVybrane([]);
        setNaAktualizaciu([]);
        poImporte();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenia sa nepodarilo pridať.');
      })
      .finally(() => setBezi(false));
  };

  const prepniAutomatikuKlik = (): void => {
    setChyba(undefined);
    void prepniAutomatiku(!automatika)
      .then(poZmene)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Nastavenie sa nepodarilo uložiť.');
      });
  };

  const spustiOdobranie = (): void => {
    if (!confirm(`Odobrať účet ${nazov}? Zariadenia a ich kľúče zostanú.`)) return;
    setChyba(undefined);
    void odober()
      .then(poZmene)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Účet sa nepodarilo odobrať.');
      });
  };

  return (
    <li>
      <Card className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-medium text-(--color-text)">{nazov}</span>
            <span className="text-xs break-words text-(--color-text-muted)">{popis}</span>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="primary"
              icon={<IconRefresh className="size-4" />}
              onClick={spustiSync}
              disabled={bezi}
            >
              {bezi ? 'Načítavam…' : 'Synchronizovať'}
            </Button>
            <Button
              variant="danger"
              icon={<IconTrash className="size-4" />}
              onClick={spustiOdobranie}
            >
              Odobrať
            </Button>
          </div>
        </div>

        {/*
          Automatika siaha von zo siete sama od seba, takze je to volba
          a je predvolene vypnuta. Doplna LEN kluc k zariadeniu, ktore sa
          v sieti ohlasilo - nove zariadenia nepridava (2026-09-18).
        */}
        <label className="flex items-start gap-2 text-sm text-(--color-text)">
          <input
            type="checkbox"
            checked={automatika}
            onChange={prepniAutomatikuKlik}
            className="mt-1"
          />
          <span>
            Dopĺňať kľúče automaticky
            <span className="block text-xs text-(--color-text-muted)">
              Keď sa v sieti ohlási známe zariadenie bez kľúča, brána z tohto účtu doplní kľúč, meno
              aj profil. Nové zariadenia nepribudnú — tie si vyberáš tu.
            </span>
          </span>
        </label>

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

        {zapisane !== undefined && (
          <Notice tone="ok">
            Pridané zariadenia: {zapisane.added}, aktualizované kľúče: {zapisane.updated}.
          </Notice>
        )}

        {vysledok !== undefined && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-(--color-text)">
              Z účtu prišlo {vysledok.total} zariadení, {vysledok.known} už poznáme.
            </p>

            {vysledok.newDevices.length === 0 &&
              vysledok.changedKeys.length === 0 &&
              vysledok.missingKeys.length === 0 && (
                <p className="text-sm text-(--color-text-muted)">
                  Nič nové a žiadny zmenený kľúč — brána má všetko, čo účet vie.
                </p>
              )}

            {vysledok.missingKeys.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-(--color-text)">
                  Chýba kľúč ({vysledok.missingKeys.length})
                </legend>
                <p className="text-xs text-(--color-text-muted)">
                  Zariadenie brána pozná, ale kľúč k nemu nemá. Doplnením sa nič neprepíše, preto sú
                  predvolene zaškrtnuté. Spolu s kľúčom sa doplní aj meno a profil.
                </p>
                {vysledok.missingKeys.map((d) => (
                  <label key={d.deviceId} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={naAktualizaciu.includes(d.deviceId)}
                      onChange={() => prepni(naAktualizaciu, setNaAktualizaciu, d.deviceId)}
                    />
                    <span>
                      {d.name}
                      <span className="block text-xs text-(--color-text-muted)">{d.deviceId}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}

            {vysledok.newDevices.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-(--color-text)">
                  Nové zariadenia ({vysledok.newDevices.length})
                </legend>
                {vysledok.newDevices.map((d) => (
                  <label key={d.deviceId} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={vybrane.includes(d.deviceId)}
                      onChange={() => prepni(vybrane, setVybrane, d.deviceId)}
                    />
                    <span>
                      {d.name}
                      <span className="block text-xs text-(--color-text-muted)">
                        {d.deviceId}
                        {d.category !== undefined && ` · ${d.category}`}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}

            {vysledok.changedKeys.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-(--color-text)">
                  Zmenený kľúč ({vysledok.changedKeys.length})
                </legend>
                <p className="text-xs text-(--color-text-muted)">
                  Kľúč sa mení pri prepárovaní v aplikácii. So starým už zariadenie nekomunikuje —
                  prepíše sa len to, čo tu potvrdíš.
                </p>
                {vysledok.changedKeys.map((d) => (
                  <label key={d.deviceId} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={naAktualizaciu.includes(d.deviceId)}
                      onChange={() => prepni(naAktualizaciu, setNaAktualizaciu, d.deviceId)}
                    />
                    <span>
                      {d.name}
                      <span className="block text-xs text-(--color-text-muted)">{d.deviceId}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}

            {(vysledok.newDevices.length > 0 ||
              vysledok.changedKeys.length > 0 ||
              vysledok.missingKeys.length > 0) && (
              <div>
                <Button
                  variant="primary"
                  onClick={zapis}
                  disabled={bezi || (vybrane.length === 0 && naAktualizaciu.length === 0)}
                >
                  {bezi ? 'Zapisujem…' : 'Pridať vybrané'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </li>
  );
}

/** Kedy sa naposledy synchronizovalo; spolocny text pre obe karty. */
function popisSynchronizacie(lastSyncAt: string | null): string {
  return lastSyncAt === null
    ? 'zatiaľ nesynchronizované'
    : `naposledy ${new Date(lastSyncAt).toLocaleString('sk-SK')}`;
}

/** Pridanie účtu. Tajomstvo aj heslo idú na server a späť sa nevracajú. */
function FormularUctu({
  poPridani,
  zavri,
}: {
  readonly poPridani: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [udaje, setUdaje] = useState({
    label: '',
    clientId: '',
    secret: '',
    email: '',
    password: '',
    ttid: '',
  });
  const [region, setRegion] = useState<TuyaAppRegion>('eu');
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const pole = (kluc: keyof typeof udaje) => (hodnota: string) =>
    setUdaje((doteraz) => ({ ...doteraz, [kluc]: hodnota }));

  const odosli = (event: FormEvent): void => {
    event.preventDefault();
    setChyba(undefined);
    setBezi(true);
    void tuya
      .addAppAccount({ ...udaje, region })
      .then(() => {
        // Heslo v prehliadači nedržíme dlhšie, než treba.
        setUdaje({ label: '', clientId: '', secret: '', email: '', password: '', ttid: '' });
        poPridani();
        zavri();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Účet sa nepodarilo uložiť.');
      })
      .finally(() => setBezi(false));
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Nový účet</SectionTitle>

      <Notice tone="warn">
        <p className="font-medium">Heslo sa uloží.</p>
        <p className="mt-1">
          Uloží sa zašifrované, aby sa kľúče dali stiahnuť aj po pridaní ďalšieho zariadenia v
          telefóne — inak by sa zadávalo znova pri každej synchronizácii. Účet sa dá kedykoľvek
          odobrať aj s heslom.
        </p>
      </Notice>

      <form className="flex flex-col gap-3" onSubmit={odosli}>
        <Field
          label="Názov"
          value={udaje.label}
          onChange={pole('label')}
          required
          hint="Ako si značku pomenuješ ty, napríklad Smart Life alebo Gosund."
        />
        <Field
          label="Client ID"
          value={udaje.clientId}
          onChange={pole('clientId')}
          required
          hint="Z aplikácie danej značky; hovorí API, ktorá aplikácia sa pýta."
        />
        <Field
          label="Tajomstvo aplikácie"
          value={udaje.secret}
          onChange={pole('secret')}
          type="password"
          required
          hint="Nie je to krátky kľúč — býva to celý reťazec ako A_…_… (67 znakov) alebo odtlačok certifikátu a za ním dve časti (161 znakov). Vlož ho celý, nič z neho neuberaj."
        />
        <Field
          label="E-mail účtu"
          value={udaje.email}
          onChange={pole('email')}
          required
          autoComplete="off"
        />
        <Field
          label="Heslo účtu"
          value={udaje.password}
          onChange={pole('password')}
          type="password"
          required
          autoComplete="off"
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-(--color-text)">Dátové centrum</span>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value as TuyaAppRegion)}
            className="rounded-(--radius-control) border border-(--color-border) bg-(--color-surface-raised) px-3 py-2 text-sm text-(--color-text)"
          >
            <option value="eu">Európa</option>
            <option value="us">Amerika</option>
            <option value="cn">Čína</option>
            <option value="in">India</option>
          </select>
          <span className="text-xs text-(--color-text-muted)">
            To isté, ktoré si vybral v aplikácii pri registrácii — inak účet neexistuje.
          </span>
        </label>

        <Field
          label="Identifikátor aplikácie (nepovinné)"
          value={udaje.ttid}
          onChange={pole('ttid')}
          placeholder="tuya"
          hint="Aplikácia Smart Life sa hlási ako smart_life, Tuya Smart ako tuya. Prázdne pole znamená tuya. Je súčasťou podpisu, takže zlý údaj Tuya odmietne."
        />

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={bezi}>
            {bezi ? 'Ukladám…' : 'Uložiť účet'}
          </Button>
          <Button variant="ghost" onClick={zavri}>
            Zrušiť
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Výsledok stiahnutia alebo jeho chyba.
 *
 * Je to vlastný komponent, lebo sťahovať sa dá z dvoch miest — zo
 * zbalenej karty uloženým prístupom aj z formulára — a odpoveď má
 * v oboch prípadoch vyzerať rovnako.
 */
function VysledokSynchronizacieNotice({
  chyba,
  vysledok,
}: {
  readonly chyba: string | undefined;
  readonly vysledok: VysledokSynchronizacie | undefined;
}): ReactNode {
  if (chyba !== undefined) return <Notice tone="danger">{chyba}</Notice>;
  if (vysledok === undefined) return null;
  return (
    <Notice tone="ok">
      <p className="font-medium">
        Z účtu prišlo {vysledok.total} zariadení, kľúč dostalo {vysledok.added + vysledok.updated}.
      </p>
      {vysledok.withoutKey > 0 && (
        <p className="mt-1">
          {vysledok.withoutKey} zariadení kľúč nemá — býva to podriadené zariadenie za bránou, ktoré
          sa lokálne ovládať nedá.
        </p>
      )}
    </Notice>
  );
}

/** Rucne pridanie zariadenia, ked ho discovery nemoze najst. */
function FormularZariadenia({
  poPridani,
  zavri,
}: {
  readonly poPridani: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [udaje, setUdaje] = useState<{
    deviceId: string;
    name: string;
    localKey: string;
    ip: string;
    version: '3.1' | '3.2' | '3.3' | '3.4' | '3.5';
  }>(PRAZDNE_ZARIADENIE);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const pole = (kluc: keyof typeof udaje) => (hodnota: string) =>
    setUdaje((u) => ({ ...u, [kluc]: hodnota }));

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setPrebieha(true);

    const vstup: NoveTuyaZariadenie = {
      deviceId: udaje.deviceId.trim(),
      localKey: udaje.localKey.trim(),
      ...(udaje.name.trim() === '' ? {} : { name: udaje.name.trim() }),
      ...(udaje.ip.trim() === '' ? {} : { ip: udaje.ip.trim(), version: udaje.version }),
    };

    void tuya
      .add(vstup)
      .then(() => {
        setUdaje(PRAZDNE_ZARIADENIE);
        poPridani();
        zavri();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenie sa nepodarilo pridať.');
      })
      .finally(() => setPrebieha(false));
  };

  const vyber =
    'w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)';

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Pridať zariadenie ručne</SectionTitle>
      <p className="text-sm text-(--color-text-muted)">
        Identifikátor a lokálny kľúč nájdeš v synchronizácii účtu alebo na Tuya IoT Platform. Adresu
        vyplň, ak sa zariadenie neohlási samo — bez nej sa naň brána nemá ako pripojiť.
      </p>

      <form onSubmit={odosli} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Identifikátor zariadenia"
            value={udaje.deviceId}
            onChange={pole('deviceId')}
            required
            autoComplete="off"
            placeholder="bfe9f3ac13375c4e1fjfan"
          />
          <Field
            label="Názov"
            value={udaje.name}
            onChange={pole('name')}
            placeholder="Kuchyňa GU10"
          />
          <Field
            label="Lokálny kľúč"
            type="password"
            value={udaje.localKey}
            onChange={pole('localKey')}
            required
            autoComplete="off"
            hint="Uloží sa zašifrovaný; zobraziť ho môže len správca a každé zobrazenie ide do auditu."
          />
          <Field
            label="Adresa v sieti"
            value={udaje.ip}
            onChange={pole('ip')}
            placeholder="192.168.1.50"
          />
          <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
            Verzia protokolu
            <select
              className={vyber}
              value={udaje.version}
              onChange={(e) => pole('version')(e.target.value)}
            >
              {(['3.5', '3.4', '3.3', '3.2', '3.1'] as const).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <span className="text-xs font-normal text-(--color-text-subtle)">
              Nájdeš ju v aplikácii Tuya v podrobnostiach zariadenia.
            </span>
          </label>
        </div>

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={prebieha}>
            {prebieha ? 'Pridávam a pripájam…' : 'Pridať a pripojiť'}
          </Button>
          <Button variant="ghost" onClick={zavri} disabled={prebieha}>
            Zrušiť
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Ktory zdroj klucov ma byt prave videt. */
type ZdrojKlucov = 'qr' | 'ucet' | 'iot';

/*
 * Ucet aplikacie je zatial SKRYTY (rozhodnutie zadavatela 2026-09-24):
 * pouzivaju sa len QR kod a Tuya IoT. Kod zostava cely - vratit ho
 * znamena pridat sem jeden riadok.
 *
 * Ked uz nejaky ucet aplikacie existuje, volba sa ukaze aj tak: skryta,
 * ale stale funkcna cesta by bola horsia nez jedna polozka navyse.
 */
const ZDROJE: readonly { readonly id: ZdrojKlucov; readonly popis: string }[] = [
  { id: 'qr', popis: 'QR kód' },
  { id: 'iot', popis: 'Tuya IoT' },
];

const ZDROJ_UCET: { readonly id: ZdrojKlucov; readonly popis: string } = {
  id: 'ucet',
  popis: 'Účet aplikácie',
};

/**
 * Zdroje klucov za jednym prepinacom.
 *
 * Su tri a kazdy sa hodi na nieco ine, ale naraz treba vzdy len jeden -
 * vsetky tri pod sebou robili zo stranky nepriechodnu stenu (ziadost
 * zadavatela 2026-09-18). Predvolene je QR kod: je oficialny a nechce
 * ani predplatne, ani tajomstvo znacky.
 */
function ZdrojeKlucov({ poZmene }: { readonly poZmene: () => void }): ReactNode {
  const [zdroj, setZdroj] = useState<ZdrojKlucov>('qr');
  const [otvorene, setOtvorene] = useState(false);
  const qr = useApi((signal) => tuya.qrAccounts(signal));
  const ucty = useApi((signal) => tuya.appAccounts(signal));
  const cloud = useApi((signal) => tuya.cloudSettings(signal));

  /*
   * Suhrn hovori, odkial sa kluce beru, bez toho, aby sa cokolvek
   * rozbalovalo. Bez neho karta nepovedala nic a splyvala so zvyskom
   * stranky (hlasene 2026-09-18).
   */
  const uctovAplikacie = ucty.data?.accounts.length ?? 0;
  const zdroje = uctovAplikacie > 0 ? [ZDROJE[0], ZDROJ_UCET, ZDROJE[1]] : ZDROJE;

  const pocty: { id: ZdrojKlucov; popis: string; zapnuty: boolean }[] = [
    {
      id: 'qr',
      popis: `QR kód: ${(qr.data?.accounts.length ?? 0) === 0 ? 'žiadny účet' : `${qr.data?.accounts.length ?? 0} účet(y)`}`,
      zapnuty: (qr.data?.accounts.length ?? 0) > 0,
    },
    ...(uctovAplikacie > 0
      ? [
          {
            id: 'ucet' as ZdrojKlucov,
            popis: `Účet aplikácie: ${uctovAplikacie} účet(y)`,
            zapnuty: true,
          },
        ]
      : []),
    {
      id: 'iot',
      popis: `Tuya IoT: ${cloud.data?.cloud.hasSecret === true ? 'prístup uložený' : 'nenastavené'}`,
      zapnuty: cloud.data?.cloud.hasSecret === true,
    },
  ];

  const obnov = (): void => {
    qr.refresh();
    ucty.refresh();
    cloud.refresh();
    poZmene();
  };

  return (
    <Card className="flex flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <SectionTitle>Kľúče</SectionTitle>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {pocty.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 text-xs">
                <StatusDot tone={p.zapnuty ? 'ok' : 'neutral'} />
                <span className={p.zapnuty ? 'text-(--color-text)' : 'text-(--color-text-subtle)'}>
                  {p.popis}
                </span>
              </span>
            ))}
          </div>
        </div>
        <Button onClick={() => setOtvorene((o) => !o)} aria-expanded={otvorene}>
          {otvorene ? 'Skryť' : 'Spravovať'}
        </Button>
      </div>

      {otvorene && (
        <div className="flex flex-col gap-3 border-t border-(--color-border) pt-3">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Zdroj kľúčov">
            {zdroje.map((z) =>
              z === undefined ? null : (
                <Button
                  key={z.id}
                  variant={zdroj === z.id ? 'primary' : 'ghost'}
                  onClick={() => setZdroj(z.id)}
                >
                  {z.popis}
                </Button>
              ),
            )}
          </div>

          {zdroj === 'qr' && <UctyQr poImporte={obnov} />}
          {zdroj === 'ucet' && <UctyAplikacie poImporte={obnov} />}
          {zdroj === 'iot' && <SynchronizaciaKlucov poSynchronizacii={obnov} />}
        </div>
      )}
    </Card>
  );
}

export function Tuya(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [pridava, setPridava] = useState(false);
  const [exportuje, setExportuje] = useState(false);
  const [chybaExportu, setChybaExportu] = useState<string | undefined>(undefined);
  const [hladanie, setHladanie] = useState('');
  const [vybrane, setVybrane] = useState<string | undefined>(undefined);
  const [diagnostika, setDiagnostika] = useState(false);
  const nacitanie = useApi((signal) => tuya.devices(signal));

  /*
   * Export klucov.
   *
   * Kluce vydava Tuya a jej cloudove predplatne ma skusobnu dobu -
   * vlastna kopia je jedina poistka proti tomu, ze sa k nim uz
   * nedostanes. Subor obsahuje pristupove kluce ku vsetkym zariadeniam,
   * takze to rozhranie povie nahlas.
   */
  const exportujKluce = (): void => {
    setChybaExportu(undefined);
    setExportuje(true);
    void tuya
      .exportKlucov()
      .then(({ fileName, csv }) => {
        const odkaz = document.createElement('a');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        odkaz.href = url;
        odkaz.download = fileName;
        odkaz.click();
        URL.revokeObjectURL(url);
      })
      .catch((error: unknown) => {
        setChybaExportu(error instanceof Error ? error.message : 'Export sa nepodaril.');
      })
      .finally(() => setExportuje(false));
  };

  /*
   * Zariadenia sa hlasia, kedy sa im chce - nie na poziadanie. Zoznam
   * sa preto sam obnovuje, kym je obrazovka otvorena; inak by na nej
   * chybalo zariadenie, ktore sa ohlasilo o par sekund neskor.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      // Potichu: `reload` by kazdych desat sekund schoval celu
      // obrazovku za stav nacitavania a vyzeralo by to ako blikanie.
      nacitanie.refresh();
    }, INTERVAL_ZOZNAMU_MS);
    return () => clearInterval(timer);
  }, [nacitanie.refresh]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Tuya"
        detail="Zariadenia v sieti. Bez lokálneho kľúča sa z nich nedá čítať ani ich ovládať."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={nacitanie.reload}
              icon={<IconRefresh className="size-4" />}
              variant="ghost"
            >
              Obnoviť
            </Button>
            {jeSpravca && (
              <>
                <Button
                  icon={<IconDownload className="size-4" />}
                  onClick={exportujKluce}
                  disabled={exportuje}
                >
                  {exportuje ? 'Pripravujem…' : 'Kľúče ako CSV'}
                </Button>
                <Button
                  variant="primary"
                  icon={<IconPlus className="size-4" />}
                  onClick={() => setPridava(true)}
                  disabled={pridava}
                >
                  Pridať ručne
                </Button>
              </>
            )}
          </div>
        }
      />

      {chybaExportu !== undefined && <Notice tone="danger">{chybaExportu}</Notice>}

      {pridava && (
        <FormularZariadenia poPridani={nacitanie.reload} zavri={() => setPridava(false)} />
      )}

      {jeSpravca && <ZdrojeKlucov poZmene={nacitanie.reload} />}

      <Data nacitanie={nacitanie}>
        {(data) => {
          const hladane = hladanie.trim().toLowerCase();
          const zobrazene =
            hladane === ''
              ? data.devices
              : data.devices.filter(
                  (d) =>
                    (d.name ?? '').toLowerCase().includes(hladane) ||
                    d.deviceId.toLowerCase().includes(hladane) ||
                    (d.ip ?? '').includes(hladane),
                );
          const pripojene = data.devices.filter((d) => d.state === 'connected').length;
          const bezKluca = data.devices.filter((d) => !d.hasLocalKey).length;
          const vybraneZariadenie = data.devices.find((d) => d.deviceId === vybrane);

          return (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-(--color-text-muted)">
                  <span className="font-medium text-(--color-text)">
                    {data.devices.length} zariadení
                  </span>{' '}
                  · {pripojene} pripojených
                  {bezKluca > 0 && ` · ${bezKluca} bez kľúča`}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="search"
                    value={hladanie}
                    onChange={(e) => setHladanie(e.target.value)}
                    placeholder="Hľadať podľa názvu, ID alebo adresy"
                    aria-label="Hľadať zariadenie Tuya"
                    className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface-raised) px-3 py-2 text-sm text-(--color-text) placeholder:text-(--color-text-subtle) sm:w-64"
                  />
                  {/*
                   * Prehliadka siete je DIAGNOSTIKA, nie bezna praca -
                   * preto je schovana za tlacidlom (ziadost zadavatela
                   * 2026-09-18).
                   */}
                  <Button
                    variant="ghost"
                    onClick={() => setDiagnostika((d) => !d)}
                    aria-expanded={diagnostika}
                  >
                    {diagnostika ? 'Skryť diagnostiku' : 'Diagnostika'}
                  </Button>
                </div>
              </div>

              {data.devices.length === 0 ? (
                <EmptyState
                  title="Zatiaľ nič nenašlo"
                  detail="Zariadenia sa hlásia samy UDP broadcastom. Musia byť v tej istej sieti ako LoxLink; diagnostika povie, či vôbec niečo prichádza."
                />
              ) : zobrazene.length === 0 ? (
                <EmptyState
                  title="Nič sa nenašlo"
                  detail={`Výrazu „${hladanie}" nezodpovedá žiadne zariadenie.`}
                />
              ) : (
                <Card className="overflow-hidden p-0">
                  {/* Hlavicka stlpcov je len na sirsej obrazovke; na mobile su udaje pod nazvom. */}
                  <div className="hidden grid-cols-[minmax(0,2.2fr)_7rem_minmax(0,2.4fr)_minmax(0,1.4fr)_auto] gap-3 border-b border-(--color-border) px-3 py-2 text-xs text-(--color-text-subtle) sm:grid">
                    <span>Názov</span>
                    <span>Stav</span>
                    <span>Identifikátor</span>
                    <span>Adresa</span>
                    <span />
                  </div>
                  <ul className="flex flex-col">
                    {zobrazene.map((d) => (
                      <RiadokZariadenia
                        key={d.deviceId}
                        device={d}
                        otvorene={d.deviceId === vybrane}
                        onPrepni={() =>
                          setVybrane((teraz) => (teraz === d.deviceId ? undefined : d.deviceId))
                        }
                      />
                    ))}
                  </ul>
                </Card>
              )}

              {vybraneZariadenie !== undefined && (
                <DetailZariadenia
                  device={vybraneZariadenie}
                  poZmene={nacitanie.refresh}
                  zavri={() => setVybrane(undefined)}
                />
              )}

              {diagnostika && (
                <Diagnostika
                  prazdne={data.devices.length === 0}
                  znameAdresy={data.devices.map((d) => d.ip).filter((ip): ip is string => !!ip)}
                  poHladani={nacitanie.refresh}
                />
              )}
            </div>
          );
        }}
      </Data>
    </div>
  );
}

/**
 * Vysledok prehliadky siete.
 *
 * Podstatne su adresy, ktore brana este NEPOZNA: to su zariadenia,
 * ktore sa neohlasuju a discovery ich nikdy nenajde. Pridat sa daju
 * rucne - potrebny je ich identifikator a lokalny kluc.
 */
function VysledokPrehliadky({
  scan,
  zname,
}: {
  readonly scan: VysledokScanu;
  readonly zname: readonly string[];
}): ReactNode {
  const nove = scan.found.filter((ip) => !zname.includes(ip));

  return (
    <div className="flex flex-col gap-2 rounded-(--radius-control) border border-(--color-border) p-3">
      <span className="text-sm font-medium text-(--color-text)">Prehliadka siete</span>
      <p className="text-xs text-(--color-text-muted)">
        Prezretých {scan.scannedHosts} adries
        {scan.subnets.length > 0 && ` (${scan.subnets.join(', ')})`}. Odpovedalo na porte 6668:{' '}
        {scan.found.length}.
      </p>

      {scan.skipped.length > 0 && (
        <p className="text-xs text-(--color-text-muted)">
          Preskočené siete (väčšie než /24): {scan.skipped.join(', ')}
        </p>
      )}

      {nove.length === 0 ? (
        <p className="text-xs text-(--color-text-muted)">
          Nič nové. Zariadenie, ktoré tu nie je ani sa nehlási, nie je v tejto sieti — býva to
          podriadené zariadenie za bránou (Zigbee, Bluetooth) alebo iná podsieť.
        </p>
      ) : (
        <>
          <p className="text-xs text-(--color-text-muted)">
            Na týchto adresách počúva zariadenie Tuya, ale samo sa neohlasuje ({nove.length}).
            Pridaj ho ručne — jeho identifikátor a kľúč sa zo spojenia zistiť nedajú.
          </p>
          {/*
           * Zoznam sa roluje sam, karta nie.
           *
           * V sieti so stovkou zariadeni by inak vysledok vytlacil vsetko
           * ostatne mimo obrazovku a tlacidlo na dalsiu prehliadku by sa
           * hladalo rolovanim cez adresy (ziadost zadavatela 2026-09-16).
           * Vysvetlenie nad zoznamom zostava vidno.
           */}
          <ul className="flex max-h-48 flex-wrap gap-2 overflow-y-auto pr-1 font-mono text-xs text-(--color-text)">
            {nove.map((ip) => (
              <li
                key={ip}
                className="h-fit rounded-(--radius-control) bg-(--color-surface-sunken) px-2 py-1"
              >
                {ip}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/*
 * Profil zariadenia.
 *
 * Profil hovori, co ktore DPS znamena. Bez neho brana surove cisla
 * neprelozi a v Loxone nie je co ovladat, takze pri zariadeni bez
 * profilu musi byt vidno PRECO - a co presne zariadenie hlasi.
 */
function Profil({ device }: { readonly device: TuyaZariadenie }): ReactNode {
  const neprelozene = device.reportedDps.filter((d) => !device.mappedDps.includes(d.dp));

  if (device.profileId !== undefined) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-(--color-text-subtle)">
        <span>
          Profil: {device.profileName ?? device.profileId}
          {device.profileDerived && ' (odvodený z Tuya IoT)'}
        </span>
        {neprelozene.length > 0 && (
          <span title={neprelozene.map((d) => `${d.dp}: ${d.code}`).join(', ')}>
            · {neprelozene.length} nepreložených DPS
          </span>
        )}
      </div>
    );
  }

  return (
    <Notice tone="warn">
      <p className="font-medium">Zariadenie nemá profil.</p>
      {device.reportedDps.length === 0 ? (
        <p className="mt-1">
          Zatiaľ nevieme, čo hlási. Synchronizuj účet cez QR kód alebo stiahni kľúče z Tuya IoT —
          spolu s kľúčom sa načíta aj špecifikácia zariadenia a profil sa z nej odvodí.
        </p>
      ) : (
        <>
          <p className="mt-1">
            Zo špecifikácie sa nedala odvodiť žiadna schopnosť — zariadenie hlási samé kódy, ktoré
            LoxLink zatiaľ nepozná. Pošli tento zoznam a doplní sa mapovanie:
          </p>
          <ul className="mt-2 flex flex-col gap-0.5 font-mono text-xs">
            {device.reportedDps.map((d) => (
              <li key={d.dp}>
                {d.dp}: {d.code} ({d.type})
              </li>
            ))}
          </ul>
        </>
      )}
    </Notice>
  );
}

/*
 * Zobrazenie lokalneho kluca.
 *
 * Kluc z brany doteraz NIKDY nevychadzal a je to plnohodnotny pristup
 * k zariadeniu. Vydava sa preto az na vyzvu, jednemu zariadeniu naraz,
 * a kazde vydanie ide do audit logu. Po minute sa schova sam - nie kvoli
 * utocnikovi, ten ho uz ma, ale aby nezostal svietit na obrazovke
 * v kancelarii (ziadost zadavatela 2026-09-17).
 */
const SKRYT_KLUC_PO_MS = 60_000;

function Kluc({ device }: { readonly device: TuyaZariadenie }): ReactNode {
  const [kluc, setKluc] = useState<string | undefined>(undefined);
  const [nacitava, setNacitava] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (kluc === undefined) return undefined;
    const timer = setTimeout(() => setKluc(undefined), SKRYT_KLUC_PO_MS);
    return () => clearTimeout(timer);
  }, [kluc]);

  const odhal = (): void => {
    setChyba(undefined);
    setNacitava(true);
    void tuya
      .odhalKluc(device.deviceId)
      .then(({ localKey }) => setKluc(localKey))
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kľúč sa nepodarilo načítať.');
      })
      .finally(() => setNacitava(false));
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {kluc === undefined ? (
        <Button onClick={odhal} disabled={nacitava}>
          {nacitava ? 'Načítavam…' : 'Zobraziť kľúč'}
        </Button>
      ) : (
        <>
          <code className="rounded-(--radius-control) bg-(--color-surface-sunken) px-2 py-1 font-mono text-(--color-text) select-all">
            {kluc}
          </code>
          <Button variant="ghost" onClick={() => setKluc(undefined)}>
            Skryť
          </Button>
          <span className="text-(--color-text-subtle)">Skryje sa sám do minúty.</span>
        </>
      )}
      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
    </div>
  );
}
